

import React from "react";
import { IconLoader2 } from "@tabler/icons-react";

type PrimaryButtonProps = {
  text: string; // Button text
  onClick?: (e: React.MouseEvent) => void; // Click handler
  onHover?: () => void; // Hover handler
  icon?: React.ElementType; // Icon before text (or after, with iconPosition="right")
  iconPosition?: "left" | "right"; // Icon position relative to text (P8)
  size?: "big" | "medium" | "small" | "xsmall" | "xssmall"; // Sizes
  color?: string; // Color
  textColor?: string; // Text color
  width?: string; // Width
  type?: "button" | "submit"; // Button type
  disabled?: boolean; // Disabled state
  loading?: boolean; // Loading state
  margin?: string; // Margin
  iconSize?: number; // Icon size
  rounded?: string;
  fontType?: string; // Font type
  iconMargin?: string; // Icon margin
  height?: string; // Height
  className?: string; // Additional class names
  paddingOverride?: string; // Padding override
  testId?: string;
  /** Accessible name — required for icon-only buttons (text=""), matching
   *  SecondaryButton and IconButton. Defaults to `text`. */
  ariaLabel?: string;
  /**
   * Id of the element explaining WHY this button is disabled.
   *
   * ⚠️ It is not enough ON ITS OWN when the button is NATIVELY disabled: the
   * control is then out of the tab order, so nothing can focus it and a
   * `aria-describedby` hung on it is never spoken. The description needs a
   * second channel, and there are two that work:
   *
   *  1. the notice it points AT is a live region (`role="alert"` /
   *     `aria-live="polite"`), so the reason announces itself when it appears
   *     — what `PSendEmail`, `PImportCore` and `PSeatingExport` do, and the
   *     right answer when the reason is a whole visible box; or
   *  2. {@link focusableWhenDisabled}, which keeps the button in the tab
   *     order so the description is read on focus — the right answer when the
   *     reason is an existing inline node the operator may want to re-read on
   *     demand, as the wallet crop dialog's readout is.
   *
   * With neither, the attribute is decoration. This prop stays useful in both
   * arrangements: in (1) it is what browse/virtual-cursor mode reads.
   */
  ariaDescribedBy?: string;
  /**
   * Opt in to the "soft disabled" pattern: while `disabled`, the button keeps
   * the native attribute OFF and carries `aria-disabled="true"` instead, so it
   * stays in the tab order — and its activation is a no-op (the click is
   * swallowed and `preventDefault`ed, so a `type="submit"` cannot submit).
   *
   * Why it is opt-in: ~214 call sites pass `disabled`, and for most of them a
   * dead control the user can still tab onto is noise. Use it where the
   * button's OFF state is something the operator has to be able to find out
   * about — i.e. wherever {@link ariaDescribedBy} names a reason, as the crop
   * dialog's Apply does ("Too small — at least 750 px wide").
   *
   * The name is MUI Base's (`useButton`'s `focusableWhenDisabled`).
   */
  focusableWhenDisabled?: boolean;
};
export const PrimaryButton: React.FC<PrimaryButtonProps> = React.memo(function PrimaryButton({
  text,
  onClick,
  onHover,
  icon: Icon,
  iconPosition = "left",
  size = "small", // Default to medium size
  color = "cta-primary", // Default background color
  textColor = "font-light-primary", // Default text color
  width = "w-full",
  type = "button",
  disabled = false,
  loading = false,
  margin = "",
  iconSize = 20,
  rounded,
  height = "h-max",
  fontType,
  iconMargin = "mr-1",
  className = "",
  paddingOverride,
  testId,
  ariaLabel,
  ariaDescribedBy,
  focusableWhenDisabled = false,
}) {
  // Disabled, but still focusable and still announced as disabled. Everything
  // that LOOKS disabled keys on `disabled` below, unchanged; only the
  // attribute that removes the button from the tab order moves.
  const softDisabled = disabled && focusableWhenDisabled;
  const sizeStyles = {
    big: `body-1-medium ${rounded ? rounded : "rounded-lg"}`,
    medium: `body-1-medium ${rounded ? rounded : "rounded-lg"}`,
    small: `body-2-medium ${rounded ? rounded : "rounded-lg"}`,
    xsmall: `body-3-medium ${rounded ? rounded : "rounded-md"}`,
    xssmall: `body-4-bold ${rounded ? rounded : "rounded-md"}`,
  };

  const defaultPadding = {
    big: "px-6 py-3",
    medium: "px-3 py-2",
    small: "px-2 py-1",
    xsmall: "px-2 py-1",
    xssmall: "px-1.5 py-1",
  };

  const padding = paddingOverride ?? defaultPadding[size];

  const buttonStyles = `
      ${sizeStyles[size]} 
      ${padding}
      ${color}
      ${textColor}
    `;

  return (
    <button
      style={{ fontFamily: fontType }}
      type={type}
      disabled={disabled && !softDisabled}
      aria-disabled={softDisabled || undefined}
      // Emitted ONLY when the name would otherwise be missing:
      //   - `loading` hides the label span below with Tailwind `invisible`
      //     (visibility:hidden), which removes it from the accessibility
      //     tree and leaves the button nameless for the whole request;
      //   - an icon-only call site (`text=""`) has no name to begin with
      //     and must pass `ariaLabel`, as SecondaryButton and IconButton
      //     already require.
      //
      // ⚠️ Deliberately NOT set on an idle button whose text is visible.
      // The name is already exactly that text, so the attribute would add
      // nothing — but Playwright's `getByLabel` matches ANY element with a
      // non-empty aria-label, substring and case-insensitively. Labelling
      // all ~350 buttons put "Continue with Email" next to
      // `<label for="email">Email</label>` and made
      // `getByLabel("Email")` resolve to two elements: 40 e2e call sites
      // across 11 files, plus 8 more on a "Confirm Password" button whose
      // text is byte-identical to its field's label.
      //
      // ⚠️ `aria-label=""` is IGNORED by the accessible-name algorithm
      // (it falls through to the contents), so emitting `undefined` rather
      // than an empty string keeps an unlabelled icon-only button findable
      // by an audit instead of papering over it.
      aria-label={ariaLabel || (loading ? text : "") || undefined}
      aria-describedby={ariaDescribedBy}
      aria-busy={loading}
      data-testid={testId}
      // `focus-ring`, the house utility. This component never had one: it rode
      // the UA `:focus-visible` ring, which was tolerable only while a
      // disabled PrimaryButton could not be focused at all. With
      // `focusableWhenDisabled` one can be — at `opacity-50`, which composites
      // the outline too — so the indicator has to be the app's own 2px ring
      // (`--color-outline-focus`, near-black in light, near-white in dark),
      // which still clears WCAG 1.4.11's 3:1 once halved (pinned).
      className={`relative flex flex-row focus-ring ${height} justify-center items-center transition-all duration-200 ${buttonStyles} ${className} ${width} ${
        disabled ? "opacity-50" : "hover:opacity-90"
      } ${softDisabled ? "cursor-not-allowed" : ""} ${margin} `}
      // A no-op activation, not a missing handler: `aria-disabled` is a label
      // and stops nothing on its own, and a `type="submit"` would still submit
      // its form on Enter.
      onClick={softDisabled ? (e) => e.preventDefault() : onClick}
      onMouseEnter={onHover}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <IconLoader2 className="animate-spin" size={20} />
        </span>
      )}
      <span
        className={`flex flex-row items-center justify-center ${loading ? "invisible" : ""}`}
      >
        {Icon && iconPosition === "left" && (
          <Icon size={iconSize} className={`${color} ${iconMargin}`} />
        )}
        {text}
        {Icon && iconPosition === "right" && (
          <Icon size={iconSize} className={`${color} ${iconMargin === "mr-1" ? "ml-1" : iconMargin}`} />
        )}
      </span>
    </button>
  );
});
